import {
  CaptureItemParams,
  Sensitivity,
  UpdatePage,
} from '@buncha/itemDetails/types/ItemScanner';
import {CustomText, H3Text} from '@buncha/components/core/text';
import {HomeScreens, ShopperScreens} from '@buncha/home/navigation/types';
import {
  ItemScannerAction,
  useItemScannerContext,
  useUPCOrder,
  useUpdateItemDetails,
} from '../../../../itemDetails/hooks/ItemScanner';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {getClientType, getStoreName} from '@buncha/common/types/common';
import {useAppDispatch, useAppSelector} from '@buncha/config/redux/hooks';

import {Analytics} from '@buncha/config/analytics/analytics';
import AppModal from '@buncha/common/components/CustomModal/AppModal';
import {B2BScreens} from '@buncha/B2B/navigation/types';
import {BottomButton} from '../../../../components/composites/Button';
import {EventType} from '@buncha/config/analytics/type';
import If from '../../../../components/conditional/If';
import {ItemScannerGlobalActions} from '@buncha/itemDetails/reducer';
import {Modules} from '@buncha/utils/types';
import {OrderItemStatus} from '@buncha/batchDetails/types';
import {SpinnerComponent} from '@buncha/components/core/loader';
import {StyleSheet} from 'react-native';
import {Touchable} from '@buncha/components/core/touchable';
import font from '@buncha/appStyles/style/font';
import {navigation} from '@buncha/config/navigation';
import {useColorThemeType} from '../../../../appStyles/hooks/theme';
import {useGetLocale} from '@buncha/localization/hooks';
import {useIsMeijerStore} from '@buncha/home/hooks/home';
import {useRoute} from '@react-navigation/native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';

interface Props {
  isUPCMode: boolean;
  unableToScanHandler: (() => void) | undefined;
  hasUnableToScanVisible: boolean;
  isAdjacentOosItem?: boolean;
  scannedUpc: string | undefined;
  loading: boolean;
}

const ScanAgainButton = (props: Props) => {
  const {state, dispatch} = useItemScannerContext();
  const theme = useColorThemeType();
  const {updateItemDetails, updateLoading} = useUpdateItemDetails();
  const route = useRoute();
  const routeParams: any = route?.params;
  const oldRunId = routeParams?.oldRunId;
  const runId = routeParams?.runId;
  const returnPage = routeParams?.returnPage;
  const {markItemAsScanned, markingItem} = useUPCOrder(dispatch);
  const {itemLocationData, upcOrderItem, selectedCatalogStoreForUPCRun} =
    useAppSelector(gState => gState.itemScanner);
  const {title, body, button} =
    useGetLocale(Modules.itemDetails, ShopperScreens.ItemDetails) ?? {};
  const appDispatch = useAppDispatch();
  const storeId = state?.item?.order?.run?.store?.id ?? 0;
  const insets = useSafeAreaInsets();
  const hasSafeArea = insets.top > 0 || insets.bottom > 0;

  const isMeijer = useIsMeijerStore(storeId);

  const [open, setOpen] = useState(false);

  const [showButton, setShowButton] = useState(false);

  const toggleModal = useCallback(() => {
    setOpen(!open);
  }, [open]);

  const scannerExpanded = state.scannerExpanded;
  const remainingCustomers = state.remainingCustomers;

  const item = state.item;

  const buyerData = state.buyerData;
  const allFields = buyerData?.[0]?.whatPageToShow === UpdatePage.carousel;

  const startScanner = useCallback(() => {
    if (dispatch) {
      dispatch(ItemScannerAction.setWrongScanCatalogItemId(undefined));
      dispatch(ItemScannerAction.setScannerActive(true));
    }
  }, [dispatch]);

  const buttonDisabled = useMemo(() => {
    if (scannerExpanded) return false;
    if (remainingCustomers) return true;
    return false;
  }, [remainingCustomers, scannerExpanded]);

  const navigateToRun = useCallback(() => {
    if (returnPage === ShopperScreens.OrderPackingPage)
      return navigation.navigate(HomeScreens.Shopper, {
        screen: ShopperScreens.OrderPackingPage,
        params: {runId: oldRunId ?? runId},
      });
    navigation.navigate(HomeScreens.Shopper, {
      screen: ShopperScreens.BatchDetailsPage,
      params: {runId: oldRunId ?? runId},
    });
  }, [oldRunId, returnPage, runId]);

  const handlAnalytics = useCallback(() => {
    if (!item || !state.buyerData) return;
    const sensitivity = state.buyerData?.[0]?.sensitivity;
    const sensitivityText =
      sensitivity &&
      [
        Sensitivity.priceAndWeightSensitive,
        Sensitivity.priceSensitive,
        Sensitivity.weightSensitive,
      ].includes(sensitivity)
        ? 'Weight sensitive'
        : 'Quantity sensitive';
    if (item?.isCustomItem)
      return Analytics.eventWithProps('Update Price', {
        type: EventType.cta,
        runId: runId,
        orderId: item?.order?.id,
        orderItemId: routeParams?.orderItemId,
        orderItemName: item?.name,
        storeId: item?.order?.run?.store.id,
        pageType: 'Custom Item',
        client: getClientType(isMeijer),
        storeType: getStoreName(String(storeId)),
      });
    return Analytics.eventWithProps('Update Price', {
      type: EventType.cta,
      runId: runId,
      orderId: item?.order?.id,
      orderItemId: routeParams?.orderItemId,
      orderItemName: item?.name,
      storeId: item?.order?.run?.store.id,
      pageType: sensitivityText,
      client: getClientType(isMeijer),
      storeType: getStoreName(String(storeId)),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, state.buyerData]);

  const params = useMemo(() => {
    return state.formData
      .filter(data => !data.dropped)
      .map(data => {
        return {
          orderItemId: data.orderItemId,
          quantity: data.quantity,
          price: isMeijer
            ? state?.item?.price
            : Number((Number(data?.totalPrice) * 100).toFixed(0)),
          weightInfo: isMeijer
            ? [
                {
                  weight: state?.item?.orderItemAttribute?.weight,
                  unit: state?.item?.orderItemAttribute?.units,
                },
              ]
            : data.weightInfo?.filter(weight => weight.weight),
        };
      });
  }, [
    isMeijer,
    state.formData,
    state?.item?.orderItemAttribute?.units,
    state?.item?.orderItemAttribute?.weight,
    state?.item?.price,
  ]);

  const navigateToBatchPage = useCallback(() => {
    navigation.navigate(HomeScreens.Shopper, {
      screen: ShopperScreens.BatchDetailsPage,
      params: {
        isUPCMode: true,
        upcOrderId: selectedCatalogStoreForUPCRun?.id,
        runId: 0,
      },
    });
    if (appDispatch) appDispatch(ItemScannerGlobalActions.clearItemState());
  }, [appDispatch, selectedCatalogStoreForUPCRun?.id]);

  const modalConfirmation = useCallback(() => {
    const markingData: Omit<CaptureItemParams, 'isUpcOrder'> = {
      orderItemId: upcOrderItem?.id ?? 0,
      aisle: itemLocationData?.locationDescription?.aisle ?? '',
      storeContext: itemLocationData?.locationDescription?.context ?? '',
      locationImage: itemLocationData?.imageUrl,
      price: Math.floor(itemLocationData?.price ?? 0),
      esIndex: selectedCatalogStoreForUPCRun?.esIndex ?? '',
    };
    markItemAsScanned(markingData, navigateToBatchPage);
  }, [
    itemLocationData?.imageUrl,
    itemLocationData?.locationDescription?.aisle,
    itemLocationData?.locationDescription?.context,
    itemLocationData?.price,
    markItemAsScanned,
    navigateToBatchPage,
    selectedCatalogStoreForUPCRun?.esIndex,
    upcOrderItem?.id,
  ]);

  const handleButtonPress = useCallback(async () => {
    if (props?.isUPCMode) {
      toggleModal();
    } else {
      if (scannerExpanded) {
        Analytics.eventWithProps('retry barcode scanning', {
          type: EventType.cta,
          runID: runId,
          client: getClientType(isMeijer),
          storeType: getStoreName(String(storeId)),
        });
        return startScanner();
      }
      if (!params?.length) {
        Analytics.eventWithProps('Barcode proceed', {
          type: EventType.cta,
          runID: runId,
          source: 'Item Scanner Page',
          client: getClientType(isMeijer),
          storeType: getStoreName(String(storeId)),
        });
        navigateToRun();
      }

      const res = await updateItemDetails(
        params,
        props.isAdjacentOosItem,
        runId,
        isMeijer,
        props?.scannedUpc,
        returnPage === B2BScreens.B2BCheckout ||
          returnPage === ShopperScreens.OrderPackingPage
          ? OrderItemStatus.packed
          : undefined,
      );
      handlAnalytics();
      if (res) navigateToRun();
    }
  }, [
    handlAnalytics,
    isMeijer,
    navigateToRun,
    params,
    props.isAdjacentOosItem,
    props?.isUPCMode,
    props?.scannedUpc,
    returnPage,
    runId,
    scannerExpanded,
    startScanner,
    storeId,
    toggleModal,
    updateItemDetails,
  ]);

  const getButtonText = useMemo(() => {
    if (props.isUPCMode) return button?.continue;
    if (scannerExpanded) return button?.retryScanning;
    if (!params?.length) return button?.proceed;
    if (!allFields) return button?.updateQuantity;
    return button?.updatePriceQuantity;
  }, [
    allFields,
    button?.continue,
    button?.proceed,
    button?.retryScanning,
    button?.updatePriceQuantity,
    button?.updateQuantity,
    params?.length,
    props.isUPCMode,
    scannerExpanded,
  ]);

  const getBody = useMemo(() => {
    return <H3Text style={[styles.childTypo]}>{body?.updateUPC}</H3Text>;
  }, [body?.updateUPC]);

  const unableToScanCheck = useMemo(() => {
    return (
      props?.hasUnableToScanVisible &&
      !(updateLoading || markingItem || state.scanAgainButtonLoading)
    );
  }, [
    markingItem,
    props?.hasUnableToScanVisible,
    state.scanAgainButtonLoading,
    updateLoading,
  ]);

  useEffect(() => {
    const status =
      !state.scannerActive ||
      (props?.isUPCMode && state?.upcCrowdSourceProcessCompleted);
    if (status)
      setTimeout(() => {
        setShowButton(true);
      }, 500);
    else setShowButton(false);
  }, [
    props?.isUPCMode,
    state.scannerActive,
    state?.upcCrowdSourceProcessCompleted,
  ]);

  const unableToScanTextVisible = useMemo(() => {
    return unableToScanCheck && scannerExpanded && !isMeijer;
  }, [isMeijer, scannerExpanded, unableToScanCheck]);

  const marginBottom = useMemo(() => {
    if (hasSafeArea) return 20;
    return 10;
  }, [hasSafeArea]);

  return (
    <If condition={showButton}>
      <If condition={getButtonText}>
        <BottomButton
          disabled={
            buttonDisabled ||
            updateLoading ||
            markingItem ||
            state.scanAgainButtonLoading
          }
          isLoading={props.loading || updateLoading}
          changeStyleOnLoading
          containerStyle={{
            ...styles.buttonContainerStyle,
            ...{
              backgroundColor: theme?.background.gray[200],
              marginBottom: !unableToScanTextVisible ? marginBottom : 0,
            },
          }}
          loadingIndicator={<SpinnerComponent />}
          disabledBgColor={theme?.text.green[200]}
          showDividers={false}
          onPress={handleButtonPress}
          title={getButtonText}
        />
      </If>
      <If condition={unableToScanTextVisible}>
        <Touchable
          onPress={props?.unableToScanHandler}
          activeOpacity={1}
          style={{backgroundColor: theme?.text.gray[200]}}>
          <CustomText
            style={[
              styles.unableToScanText,
              {
                color: theme?.text.gray[100],
                fontFamily: font.fonts.muliBold,
                marginBottom: marginBottom,
              },
            ]}>
            {body?.uploadImage}
          </CustomText>
        </Touchable>
      </If>
      <AppModal
        isOpen={open}
        onClose={toggleModal}
        topCTAHandelr={modalConfirmation}
        bottomCTAHandelr={toggleModal}
        title={title?.areYouSuree}
        modalBody={getBody}
        ctaBottomTitle={button?.cancel}
        ctaTopTitle={button?.confirm}
        topCTAColor={theme?.background.green[100]}
        loading={updateLoading || markingItem}
      />
    </If>
  );
};

const styles = StyleSheet.create({
  jCC: {
    flexDirection: 'column',
    justifyContent: 'center',
  },
  rowFlex: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  headerTypo: {
    fontFamily: font.fonts['Mortise-X-Bold'],
    ...font.FontSize.fontH2,
  },
  childTypo: {
    ...font.FontSize.fontH6,
  },
  unableToScanText: {
    textDecorationLine: 'underline',
  },
  buttonContainerStyle: {
    paddingBottom: 0,
    paddingTop: 0,
  },
});

export default React.memo(ScanAgainButton);
